import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Icon from '../../../components/AppIcon';
import Footer from '../../../components/ui/Footer';

// =====================
// CONTENT EDITING SECTION
// =====================
const blogContent = {
    title: 'Top In-Demand Skills for the 2026 Indian Job Market: Beyond the Degree',
    author: 'JobAspirant Team',
    date: '2026-01-02',
    readTime: '8 min read',
    category: 'Market Trends',
    image: '/images/blog/skills-2026-india.png',

    // SEO Meta Tags
    metaDescription: 'Explore the 2026 shift in Indian hiring. Learn why major employers are moving toward skill-based hiring and how to use gap analysis to stay ahead.',
    keywords: 'Indian job market 2026, skill based hiring India, jobaspirant gap analysis, tech skills India, upskilling roadmap freshers',

    // Blog Post Content Sections
    sections: [
        {
            type: 'paragraph',
            content: 'The 2026 Indian job market is witnessing a fundamental shift. With the massive growth of Global Capability Centers (GCCs) and tech disruptions, "Normal" job-hunting techniques are failing. Indian employers are moving from "Degree-First" to "Skill-First" hiring. Today, a candidate with a specific skill-match is 5x more likely to get a callback than one with just a high GPA.',
        },
        {
            type: 'heading',
            content: '1. The "AI-Plus" Professional',
        },
        {
            type: 'paragraph',
            content: 'In 2026, you don’t need to be an AI engineer, but you must be an "AI-Plus" professional. Whether you are in HR, Marketing, or Finance, employers expect you to leverage automation and tools to drive ROI. Knowing how to prompt, automate workflows, and use domain-specific tools is now a baseline requirement in top Indian MNCs.',
        },
        {
            type: 'heading',
            content: '2. Data-Driven Decision Making',
        },
        {
            type: 'paragraph',
            content: 'Data is no longer just for the IT department. From Sales to Teaching, the ability to interpret a dashboard and suggest a solution based on numbers is a premium skill. Companies like Reliance, TCS, and Indian startups are prioritizing candidates who can "speak data" to solve business problems.',
        },
        {
            type: 'heading',
            content: '3. Cross-Cultural & Virtual Collaboration',
        },
        {
            type: 'paragraph',
            content: 'With India becoming the global office for the world, your ability to communicate effectively across time zones and cultures is critical. This isn\'t just about "good English"—it\'s about concise documentation, asynchronous collaboration, and virtual leadership skills.',
        },
        {
            type: 'callout',
            content: 'The Problem: Major job portals show you the door, but they don\'t give you the key. They won\'t tell you which specific skill is missing for a "Product Manager" role at a top GCC. JobAspirant solves this by pinpointing your exact skill gaps.',
        },
        {
            type: 'heading',
            content: '4. 2026 Industry-Specific Technical Focus',
        },
        {
            type: 'paragraph',
            content: 'Based on recent hiring trends in India, these are the "Must-Have" technical clusters for 2026:',
        },
        {
            type: 'list',
            items: [
                'Tech & IT: GenAI Engineering, Cloud Security (AWS/Azure), and Low-Code/No-Code Development.',
                'Finance & Fintech: ESG Reporting, Financial Modeling, and Blockchain Compliance.',
                'Marketing: Performance Marketing, AI Content Orchestration, and Consumer Behavioral Analytics.',
                'Core Engineering: IoT Integration, Sustainable Manufacturing, and Predictive Maintenance.',
                'Healthcare: Health-Informatics and Remote Patient Monitoring Tech.',
            ],
        },
        {
            type: 'heading',
            content: 'The Solution: From "Apply and Pray" to "Target and Land"',
        },
        {
            type: 'paragraph',
            content: 'The biggest mistake Indian candidates make is sending the same resume to every portal. Major portals don\'t provide a structured roadmap; they leave you in a "Black Box." To stand out, you need to identify the exact gap between your current resume and the specific Job Description (JD).',
        },
        {
            type: 'heading',
            content: 'Conclusion: Building Your Upskilling Roadmap',
        },
        {
            type: 'paragraph',
            content: 'Stop "panic-learning" random courses. In 2026, targeted upskilling is the only way to remain competitive. You need to know exactly what to learn and how to showcase it. JobAspirant’s curated roadmaps ensure you spend your time only on what matters for your target role.',
        },
    ],
};

const BlogPost = () => {
    return (
        <>
            <Helmet>
                <title>{blogContent.title} | JobAspirant Blog</title>
                <meta name="description" content={blogContent.metaDescription} />
                <meta name="keywords" content={blogContent.keywords} />
                <link rel="canonical" href="https://jobaspirant.qubitlyventures.com/blog/top-skills-employers-looking-for-2026" />

                {/* Open Graph / Facebook */}
                <meta property="og:type" content="article" />
                <meta property="og:title" content={blogContent.title} />
                <meta property="og:description" content={blogContent.metaDescription} />
                <meta property="og:image" content={blogContent.image} />

                {/* Twitter */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={blogContent.title} />
                <meta name="twitter:description" content={blogContent.metaDescription} />
                <meta name="twitter:image" content={blogContent.image} />
            </Helmet>

            <div className="min-h-screen bg-background flex flex-col">
                {/* Header */}
                <header className="border-b border-border bg-card">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex items-center justify-between">
                            <Link to="/" className="flex items-center space-x-3">
                                <div className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center">
                                    <Icon name="Zap" size={24} color="white" />
                                </div>
                                <span className="text-2xl font-bold text-foreground">JobAspirant</span>
                            </Link>
                            <Link
                                to="/blog"
                                className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
                            >
                                ← Back to Blog
                            </Link>
                        </div>
                    </div>
                </header>

                {/* Article */}
                <article className="flex-1 py-12">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Article Header */}
                        <div className="mb-8">
                            <div className="flex items-center space-x-2 mb-4">
                                <span className="text-sm font-semibold text-primary bg-primary/10 px-3 py-1 rounded">
                                    {blogContent.category}
                                </span>
                                <span className="text-sm text-muted-foreground">{blogContent.readTime}</span>
                            </div>

                            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
                                {blogContent.title}
                            </h1>

                            <div className="flex items-center space-x-4 text-sm text-muted-foreground mb-8">
                                <span>By {blogContent.author}</span>
                                <span>•</span>
                                <span>{new Date(blogContent.date).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' })}</span>
                            </div>

                            {/* Featured Image placeholder logic */}
                            <div className="aspect-video bg-muted rounded-xl overflow-hidden mb-8">
                                <img
                                    src={blogContent.image}
                                    alt={blogContent.title}
                                    className="w-full h-full object-cover"
                                    onError={(e) => {
                                        e.target.style.display = 'none';
                                        e.target.parentElement.classList.add('flex', 'items-center', 'justify-center', 'bg-gradient-to-br', 'from-primary/20', 'to-background');
                                        e.target.parentElement.innerHTML = '<div class="text-6xl">💼</div>';
                                    }}
                                />
                            </div>
                        </div>

                        {/* Article Content */}
                        <div className="prose prose-lg max-w-none">
                            {blogContent.sections.map((section, index) => {
                                switch (section.type) {
                                    case 'heading':
                                        return (
                                            <h2 key={index} className="text-2xl sm:text-3xl font-bold text-foreground mt-10 mb-4">
                                                {section.content}
                                            </h2>
                                        );

                                    case 'paragraph':
                                        return (
                                            <p key={index} className="text-lg text-muted-foreground leading-relaxed mb-6">
                                                {section.content}
                                            </p>
                                        );

                                    case 'list':
                                        return (
                                            <ul key={index} className="list-disc list-inside space-y-3 mb-8 text-lg text-muted-foreground bg-muted/30 p-6 rounded-xl">
                                                {section.items.map((item, i) => (
                                                    <li key={i} className="pl-2">
                                                        <span className="text-foreground font-medium">{item.split(':')[0]}</span>
                                                        {item.split(':')[1] ? `: ${item.split(':')[1]}` : ''}
                                                    </li>
                                                ))}
                                            </ul>
                                        );

                                    case 'callout':
                                        return (
                                            <div key={index} className="bg-primary/5 border-l-4 border-primary p-6 rounded-r-lg mb-8 italic shadow-sm">
                                                <p className="text-lg text-foreground font-medium">{section.content}</p>
                                            </div>
                                        );

                                    default:
                                        return null;
                                }
                            })}
                        </div>

                        {/* CTA Section */}
                        <div className="mt-16 p-8 bg-gradient-to-br from-primary/15 via-primary/5 to-background rounded-3xl border border-primary/20 shadow-lg text-center">
                            <h3 className="text-3xl font-bold text-foreground mb-4">
                                Bridge Your Skill Gap Today
                            </h3>
                            <p className="text-lg text-muted-foreground mb-8 max-w-2xl mx-auto">
                                Don't let your resume be a "Black Box." Get a customized gap analysis and a structured upskilling roadmap designed for the 2026 Indian market.
                            </p>
                            <Link
                                to="/login"
                                className="inline-flex items-center space-x-3 px-8 py-4 bg-primary text-primary-foreground font-bold rounded-xl hover:scale-105 transition-transform shadow-xl"
                            >
                                <span>Analyze Your Resume for Free</span>
                                <Icon name="ArrowRight" size={20} />
                            </Link>
                        </div>
                    </div>
                </article>

                <Footer />
            </div>
        </>
    );
};

export default BlogPost;